import React, { useEffect, useState, useCallback, useRef } from "react";
import ReactPaginate from "react-paginate";
import api from "../../axiosConfig";
import {
  Table,
  Button,
  InputGroup,
  FormControl,
  Container,
  Col,
} from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../pagination.css";

function getTipGrupaLabel(tip) {
  switch (tip) {
    case 1:
      return "Servicii primite";
    case 2:
      return "Stocabil";
    case 3:
      return "Servicii prestate";
    default:
      return tip || "";
  }
}

function GrupaProduseList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("q") || "";
  const [contMap, setContMap] = useState({});
  const contMapRef = useRef(contMap);

  useEffect(() => {
    contMapRef.current = contMap;
  }, [contMap]);

  const fetchConturiForPage = useCallback(
    async (pageItems) => {
      const ids = new Set();

      for (const it of pageItems || []) {
        if (it.contintrare_id) ids.add(it.contintrare_id);
        if (it.contvanzare_id) ids.add(it.contvanzare_id);
        if (it.contcheltuiala_id) ids.add(it.contcheltuiala_id);
        if (it.cont_factura_debit_id) ids.add(it.cont_factura_debit_id);
      }

      const currentMap = contMapRef.current;
      const missing = Array.from(ids).filter((id) => !(id in currentMap));
      if (missing.length === 0) return;

      try {
        const results = await Promise.all(
          missing.map(async (id) => {
            try {
              const res = await api.get(`/accounts/${id}`);
              return res.data;
            } catch (err) {
              console.warn(`Eroare la încărcare cont ID ${id}:`, err);
              return null;
            }
          }),
        );

        const patch = {};
        for (const c of results) {
          if (c && c.id) {
            patch[c.id] = c.simbol ?? c.bxsimbol ?? c.code ?? String(c.id);
          }
        }

        setContMap((prev) => ({ ...prev, ...patch }));
      } catch (err) {
        console.error("Eroare la încărcare conturi:", err);
      }
    },
    [],
  );

  // Căutare + paginare sincronizate cu backend-ul
  const fetchItems = useCallback(async () => {
    const skip = currentPage * itemsPerPage;
    try {
      const res = await api.get("/grupa_produse", {
        params: {
          search,
          skip,
          limit: itemsPerPage,
        },
      });
      setItems(res.data.items);
      setTotalCount(res.data.total);
      fetchConturiForPage(res.data.items);
    } catch (err) {
      console.error("Eroare la încărcare:", err);
    }
  }, [search, currentPage, itemsPerPage, fetchConturiForPage]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleSearchChange = (e) => {
    setSearchParams({ q: e.target.value });
    setCurrentPage(0); // reset pagină când cauți
  };

  const handleDelete = async (id) => {
    if (
      window.confirm("Sigur dorești să ștergi înregistrarea cu ID " + id + "?")
    ) {
      try {
        await api.delete(`/grupa_produse/${id}`);
        fetchItems(); // reîncărcăm lista după ștergere
      } catch (err) {
        alert("Eroare la ștergere.");
      }
    }
  };

  const pageCount = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  return (
    <Container className="mt-4">
      <h2>Grupe de produse</h2>
      <Col className="text-end">
        <Button
          className="mb-3"
          onClick={() => navigate("/grupa_produse/create")}
        >
          Adaugă
        </Button>
      </Col>
      <InputGroup className="mb-3">
        <FormControl
          placeholder="Caută după denumire"
          value={search}
          onChange={handleSearchChange}
        />
        {search && (
          <Button variant="outline-danger" onClick={() => setSearchParams({})}>
            Curăță
          </Button>
        )}
      </InputGroup>

      <div className="mb-3">
        <label>
          Rânduri pe pagină:&nbsp;
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(parseInt(e.target.value));
              setCurrentPage(0);
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </label>
      </div>

      <Table striped bordered hover>
        <thead>
          <tr>
            <th>ID</th>
            <th>Cod</th>
            <th>Denumire</th>
            <th>Cota TVA</th>
            <th>Cont Debit</th>
            <th>Cont Stoc</th>
            <th>Cont Vânzare</th>
            <th>Cont Cheltuială</th>
            <th>Tip Grupă</th>
            <th>Acțiuni</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id}>
              <td>{it.id}</td>
              <td>{it.code}</td>
              <td>{it.denumire}</td>
              <td>{it.bxvat_cod}</td>

              <td>
                {it.cont_factura_debit_id
                  ? contMap[it.cont_factura_debit_id] ||
                    it.cont_factura_debit_id
                  : ""}
              </td>
              <td>
                {it.contintrare_id
                  ? contMap[it.contintrare_id] || it.contintrare_id
                  : ""}
              </td>
              <td>
                {it.contvanzare_id
                  ? contMap[it.contvanzare_id] || it.contvanzare_id
                  : ""}
              </td>
              <td>
                {it.contcheltuiala_id
                  ? contMap[it.contcheltuiala_id] || it.contcheltuiala_id
                  : ""}
              </td>
              <td>{getTipGrupaLabel(it.tip_grupa)}</td>
              <td>
                <Button
                  size="sm"
                  onClick={() => navigate(`/grupa_produse/${it.id}`)}
                >
                  Editează
                </Button>{" "}
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDelete(it.id)}
                >
                  Șterge
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <ReactPaginate
        previousLabel={"← Înapoi"}
        nextLabel={"Înainte →"}
        pageCount={pageCount}
        forcePage={currentPage}
        onPageChange={(event) => setCurrentPage(event.selected)}
        containerClassName={"pagination justify-content-center"}
        pageClassName={"page-item"}
        pageLinkClassName={"page-link"}
        previousClassName={"page-item"}
        previousLinkClassName={"page-link"}
        nextClassName={"page-item"}
        nextLinkClassName={"page-link"}
        activeClassName={"active"}
      />
    </Container>
  );
}

export default GrupaProduseList;
